import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, pct } from "../lib/api";
import { Head, Card, Stat, Empty, Tag, KV, Disclose } from "../components/bits";
import { useAuth } from "../context/AuthContext";

const useData = (paths) => {
  const [d, setD] = useState({});
  useEffect(() => { Promise.all(paths.map((p) => api.get(p).then((r) => r.data).catch(() => null))).then((rs) => setD(Object.fromEntries(paths.map((p, i) => [p, rs[i]])))); }, []); // eslint-disable-line
  return d;
};

export function Execution() {
  const d = useData(["/trades"]);
  const trades = (d["/trades"] || []).filter((t) => t.status === "closed");
  if (!d["/trades"]) return <Empty>Loading…</Empty>;
  const fees = trades.reduce((s, t) => s + Number(t.fees || 0), 0), gross = trades.reduce((s, t) => s + Number(t.pnl || 0), 0);
  const withSl = trades.filter((t) => t.stop_loss);
  const hold = trades.filter((t) => t.close_time).map((t) => (new Date(t.close_time) - new Date(t.open_time)) / 60000).filter((m) => m > 0);
  return (
    <div data-testid="execution-page">
      <Head title="Costs & fills" sub="What your trading costs you: fees, stop discipline and how long you hold." />
      <div className="grid g4">
        <Stat label="Total fees paid" value={money(fees)} sub={gross ? `${pct((fees / Math.abs(gross)) * 100)} of gross P&L` : ""} testId="stat-fees" />
        <Stat label="Trades with a stop" value={trades.length ? pct((withSl.length / trades.length) * 100, 0) : "—"} sub={`${withSl.length} of ${trades.length}`} />
        <Stat label="Median hold" value={hold.length ? `${Math.round(hold.sort((a, b) => a - b)[Math.floor(hold.length / 2)])}m` : "—"} />
        <Stat label="Stops moved" value={trades.filter((t) => t.stop_moved).length} />
      </div>
      <div className="grid g2 mt-4">
        <Card title="Fee drag by symbol">{Object.entries(trades.reduce((m, t) => ({ ...m, [t.symbol]: (m[t.symbol] || 0) + Number(t.fees || 0) }), {})).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k, v]) => <KV key={k} k={k} v={money(v)} />)}{!trades.length && <div className="sub">No trades yet.</div>}</Card>
        <Card title="Where your costs come from">{!trades.length ? <div className="sub">Once you have trades, this shows which markets and habits cost you the most.</div> : <><KV k="Fees paid" v={money(fees)} /><KV k="Trades closed" v={trades.length} /><KV k="Trades with a stop set" v={`${withSl.length} of ${trades.length}`} /><KV k="Times you moved a stop" v={trades.filter((t) => t.stop_moved).length} /></>}</Card>
      </div>
    </div>
  );
}

export function Portfolio() {
  const d = useData(["/risk/portfolio", "/risk/status", "/accounts"]);
  const p = d["/risk/portfolio"];
  if (!p) return <Empty>Loading…</Empty>;
  const eq = Number(d["/risk/status"]?.equity || 0);
  const open = p.positions;
  const exposure = p.total_exposure;
  const risk = p.total_money_at_risk;
  const noStops = p.positions_without_stop;
  return (
    <div data-testid="portfolio-page">
      <Head title="Total risk" sub="Everything you have open right now, across every account. Open positions from linked accounts appear here automatically." />
      <div className="grid g4"><Stat label="Open positions" value={open.length} /><Stat label="Accounts" value={(d["/accounts"] || []).length} />
        <Stat label="Position size (exposure)" value={money(exposure)} sub={eq ? `${pct((exposure / eq) * 100)} of your account value` : "price × lots × units per lot"} />
        <Stat label="Money at risk if stops are hit" value={noStops === open.length && open.length ? "No stop set" : money(risk)} sub={noStops ? `${noStops} position${noStops > 1 ? "s" : ""} with no stop set` : "distance to stop × lots × units per lot"} /></div>
      <div className="grid g2 mt-4">
        <Card title="Open positions">{!open.length ? <div className="sub">No open positions. Log a trade with status "open", or link an account so your live positions appear here automatically.</div> : <div className="tbl-wrap"><table><thead><tr><th>Symbol</th><th>Market</th><th>Direction</th><th>Lots</th><th>Units per lot</th><th>Entry</th><th>Stop</th><th>Position size (exposure)</th><th>Money at risk</th></tr></thead><tbody>{open.map((t) => <tr key={t.id}><td><b>{t.symbol}</b></td><td>{t.asset_class || "Other"}</td><td>{t.direction}</td><td className="mono">{t.size}</td><td className="mono">{t.contract_size}</td><td className="mono">{t.entry_price}</td><td className="mono">{t.stop_loss ?? "No stop set"}</td><td className="mono">{money(t.exposure)}</td><td className="mono">{t.has_stop ? money(t.money_at_risk) : "No stop set"}</td></tr>)}</tbody></table></div>}</Card>
        <Card title="What you have open, by market type">{!p.by_asset_class.length ? <div className="sub">Nothing open right now.</div> : <div className="tbl-wrap"><table><thead><tr><th>Market</th><th>Positions</th><th>Exposure</th><th>Money at risk</th></tr></thead><tbody>{p.by_asset_class.map((c) => <tr key={c.asset_class}><td>{c.asset_class}</td><td className="mono">{c.positions}</td><td className="mono">{money(c.exposure)}</td><td className="mono">{money(c.money_at_risk)}</td></tr>)}</tbody></table></div>}
          {p.rates_missing?.length > 0 && <div className="note mt-3">We could not convert {p.rates_missing.join(", ")} into your account currency right now, so those figures are shown in the market's own currency.</div>}</Card>
      </div>
    </div>
  );
}

export function Research() {
  return (
    <div data-testid="research-page">
      <Head title="Test a strategy" sub="Not ready yet. This page is hidden from traders until the testing engine works." />
      <div className="grid g2">
        <Card title="What this page will do"><ul className="tight"><li>Pick a strategy you have written down, with clear entry and exit rules.</li><li>Choose the market, timeframe and dates.</li><li>We replay the price history and record the trades the rules would have taken.</li><li>Results are always labelled as a test and never mixed with your real trades.</li><li>Past results never promise future results.</li></ul></Card>
      </div>
    </div>
  );
}

export function Mentor() {
  return (
    <div data-testid="mentor-page">
      <Head title="Mentors" sub="Not ready yet. This page is hidden from traders until mentor invitations work." />
      <div className="grid g2">
        <Card title="What a mentor will see"><ul className="tight"><li>Your discipline score and streak</li><li>How often you follow your own rules, and any rule breaks</li><li>Journal entries you choose to share</li><li>When you last reviewed each strategy</li><li>Never your account numbers, balances or profit and loss, unless you switch that on</li></ul></Card>
      </div>
    </div>
  );
}

export function Market() {
  return (
    <div data-testid="market-page">
      <Head title="Strategy marketplace" sub="Not open yet. Publishing a track record to other traders needs a legal review first." />
      <div className="grid g2">
        <Card title="What a listing will need"><ul className="tight"><li>A strategy written down with clear rules</li><li>At least 100 recorded trades over at least 6 months, labelled with where the data came from</li><li>Your biggest drop from a peak, shown openly</li><li>A review by our team before it goes live</li><li>No buy or sell calls, ever</li></ul></Card>
      </div>
    </div>
  );
}
